import React, { useState } from 'react';

export interface ConsentBankAccount {
  iban: string | null;
  institution_name: string;
  is_expired: boolean;
  expires_soon: boolean;
  days_until_expiration: number;
}

interface BankConsentIndicatorProps {
  bankAccounts: ConsentBankAccount[];
  className?: string;
  children?: React.ReactNode;
}

function label(account: ConsentBankAccount): string {
  return account.iban || account.institution_name;
}

function expiredText(account: ConsentBankAccount): string {
  const daysAgo = Math.max(0, -account.days_until_expiration);
  if (daysAgo === 0) {
    return `${label(account)} — vandaag verlopen`;
  }
  return `${label(account)} — ${daysAgo} ${daysAgo === 1 ? 'dag' : 'dagen'} geleden verlopen`;
}

function expiringText(account: ConsentBankAccount): string {
  const days = account.days_until_expiration;
  return `${label(account)} — verloopt over ${days} ${days === 1 ? 'dag' : 'dagen'}`;
}

export const BankConsentIndicator: React.FC<BankConsentIndicatorProps> = ({
  bankAccounts,
  className,
  children,
}) => {
  const [visible, setVisible] = useState(false);

  const expired = bankAccounts.filter((account) => account.is_expired);
  const expiringSoon = bankAccounts.filter((account) => !account.is_expired && account.expires_soon);

  if (expired.length === 0 && expiringSoon.length === 0) {
    return children ? <>{children}</> : null;
  }

  const dotColor = expired.length > 0 ? 'bg-red-500' : 'bg-amber-500';

  return (
    <span
      className={`relative inline-flex items-center ${className || ''}`}
      tabIndex={0}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      aria-label="PSD2-verbinding vereist aandacht"
      style={{ outline: 'none' }}
    >
      {children ?? <span className={`h-2 w-2 rounded-full ${dotColor} cursor-pointer`} aria-hidden="true" />}
      {visible && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-normal w-fit min-w-60 max-w-96 z-50 px-2 py-1.5 rounded bg-gray-200 border border-gray-300 text-gray-900 text-xs shadow-lg"
          role="tooltip"
        >
          {expired.length > 0 && (
            <div>
              <p className="font-medium">PSD2-verbinding verlopen — vernieuw de koppeling:</p>
              <ul className="mt-1 list-disc pl-4">
                {expired.map((account) => (
                  <li key={label(account)}>{expiredText(account)}</li>
                ))}
              </ul>
            </div>
          )}
          {expiringSoon.length > 0 && (
            <div className={expired.length > 0 ? 'mt-2' : ''}>
              <p className="font-medium">Verloopt binnenkort — vernieuw op tijd:</p>
              <ul className="mt-1 list-disc pl-4">
                {expiringSoon.map((account) => (
                  <li key={label(account)}>{expiringText(account)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </span>
  );
};

export default BankConsentIndicator;
